import React, { useContext, useMemo } from "react";
import { Panel, Skeleton, Text, Title, Field, PageError } from "components/commons";
import lang from "translations";
import classNames from "classnames";
import { formatNumberToMoneyWithCurrencyParams } from "services/money.service";
import { useMount } from "hooks";
import style from "./booking-summary.module.scss";
import { BookingItemType, BookingSource } from "enums";
import { Divider } from "antd";
import { mixpanel } from "mixpanel";
import { VenueContext } from "contexts";
import { isEatappVenue } from "services/venue.service";

const BookingSummary = ({ data: bookingDetailsData, loading: loadingBookingDetails, error }) => {
  const { venue } = useContext(VenueContext);

  // Which labels and fields to show follows the venue. The summaryItems merge below is a
  // different question - it picks apart an EatApp payload, so it stays on the record's source.
  const isEatapp = isEatappVenue(venue);

  // Some sources never carry a currency at all (e.g. a free EatApp booking has no payment
  // attributes to read one from), so fall back to the selected venue's currency rather than
  // show a blank one.
  const displayCurrency = bookingDetailsData?.currency || venue.currencyCode;

  useMount(() => {
    mixpanel.track(TrackEvent.VisitedPage, {
      Page: lang.bookingSummary,
    });
  });

  
  const summaryItems = useMemo(() => {
    const items = bookingDetailsData?.bookingItems ?? [];
    if (bookingDetailsData?.integrationName !== BookingSource.EATAPP) {
      return items;
    }

    const merged = [];
    const indexByKey = new Map();

    items.forEach((item) => {
      if (item.quantity) {
        merged.push(item);
        return;
      }

      const key = `${item.type}-${item.name}`;
      const existingIndex = indexByKey.get(key);
      if (existingIndex === undefined) {
        indexByKey.set(key, merged.length);
        merged.push({ ...item, quantity: 1 });
        return;
      }

      const existing = merged[existingIndex];
      merged[existingIndex] = {
        ...existing,
        quantity: existing.quantity + 1,
        adultCount: (existing.adultCount ?? 0) + (item.adultCount ?? 0),
        childCount: (existing.childCount ?? 0) + (item.childCount ?? 0),
        totalAmount: (existing.totalAmount ?? 0) + (item.totalAmount ?? 0),
      };
    });

    return merged;
  }, [bookingDetailsData?.bookingItems, bookingDetailsData?.integrationName]);

  const guestCount = bookingDetailsData?.reservationGuestCount ?? 0;
  const zoneName = bookingDetailsData?.zoneName;
  const quantityText = zoneName
    ? lang.populate(lang.seatsForZone, [guestCount, zoneName])
    : lang.populate(lang.seatsQuantity, [guestCount]);
  // A row comes back for every seat whether or not its guest filled in the pre-checkin
  // form, so the completed count is the mapper's `filledGuests`, over the seat count —
  // the same pair the pre-checkin app shows as its own "Form completion".
  const formCompletionText = `${bookingDetailsData?.filledGuests ?? 0} / ${guestCount}`;
  // Kept inside the render so the label follows a language change, the way the Field
  // labels around it do. The type carries its own name in brackets: the assigned zone
  // for a zone reservation, the ticket names for a ticket one.
  const reservationTypeLabel = {
    [BookingItemType.PLACE]: lang.zone,
    [BookingItemType.TICKET]: lang.ticket,
  }[bookingDetailsData?.reservationType];
  const reservationTypeName = {
    [BookingItemType.PLACE]: zoneName,
    [BookingItemType.TICKET]: bookingDetailsData?.ticketName,
  }[bookingDetailsData?.reservationType];
  const reservationTypeText =
    reservationTypeLabel && reservationTypeName
      ? `${reservationTypeLabel} (${reservationTypeName})`
      : reservationTypeLabel;

  // `multiline` is for the fields typed into a textarea. They render exactly as they were
  // typed: `pre-wrap` keeps the line breaks and the runs of spaces alike, since both came
  // from the guest. Every other field stays on one line, as before.
  const labelText = (label = "", text, multiline = false) => {
    return (
      <Field label={label}>
        <Text className={multiline ? "whitespace-pre-wrap" : undefined}>{text ?? "-"}</Text>
      </Field>
    );
  };

  const labelArray = (label = "", array) => {
    return (
      <Field label={label}>
        {array && array.length > 0 ? (
          array.map((item, index) => (
            <Text className="whitespace-pre-wrap" key={index}>
              {item.notes}
            </Text>
          ))
        ) : (
          <Text>-</Text>
        )}
      </Field>
    );
  };

  const labelLink = (label = "", url, linkText) => {
    return (
      <Field label={label} className="min-w-0">
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title={url}
            className="text-pelorous block max-w-full break-all"
          >
            {linkText ?? url}
          </a>
        ) : (
          <Text>-</Text>
        )}
      </Field>
    );
  };

  if (error) {
    return <PageError />;
  }

  return (
    <div className="grid grid-cols-12 gap-x-4">
      <Panel className={classNames("col-span-12 md:col-span-8", style.panelHeight)}>
        <Title className="mb-lg">{isEatapp ? lang.reservationDetails : lang.bookingDetails}</Title>
        {loadingBookingDetails ? (
          <Skeleton />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-w-0">
              {labelText(
                isEatapp ? lang.reservationCode : lang.bookingNo,
                bookingDetailsData.bookingNumber
              )}
              {labelText(lang.bookedBy, bookingDetailsData.bookedBy)}
              {labelText(lang.emailAddress, bookingDetailsData.bookedByCustomer?.email)}

              {labelText(
                lang.mobileNumber,
                bookingDetailsData.bookedByCustomer?.mobileNumber === "" ||
                  !bookingDetailsData.bookedByCustomer?.mobileNumber
                  ? "-"
                  : bookingDetailsData.bookedByCustomer?.mobileNumber
              )}
              {isEatapp ? (
                <>
                  {labelText(lang.date, bookingDetailsData.arrivalDate)}
                  {labelText(lang.arrivalTime, bookingDetailsData.arrivalTime)}
                </>
              ) : (
                labelText(lang.arrivalDate, bookingDetailsData.startTime)
              )}
              {!isEatapp && labelText(lang.departureDate, bookingDetailsData.endTime)}
              {!isEatapp && labelText(lang.noOfNights, bookingDetailsData.noOfNights)}
              {/* EatApp shows the same count inside Quantity, so this would repeat it. */}
              {!isEatapp && labelText(lang.noOfGuests, guestCount)}
              {isEatapp && labelText(lang.type, reservationTypeText)}
              {isEatapp && labelText(lang.quantity, quantityText)}
              {isEatapp && labelText(lang.package, bookingDetailsData.packageName || null, true)}
              {isEatapp && labelText(lang.formCompletion, formCompletionText)}
              {!isEatapp && labelText(lang.companyDetails, bookingDetailsData.company)}
              {labelArray(lang.notes, bookingDetailsData.notes)}
              {/* The Manifest link belongs to SOAR, so a non-EatApp venue shows no link row. */}
              {isEatapp &&
                labelLink(
                  lang.manifestPublicLink,
                  bookingDetailsData.linkMiniWeb,
                  lang.clickHereLink
                )}
            </div>
          </>
        )}
      </Panel>
      <Panel className={classNames("col-span-12 md:col-span-4", style.panelHeight)}>
        <Title className="mb-lg">{isEatapp ? lang.reservationSummary : lang.bookingSummary}</Title>
        {loadingBookingDetails ? (
          <Skeleton />
        ) : (
          <>
            {/* EatApp prices a reservation as a whole and sends no money on its line items,
                so the per-item breakdown stays hidden for it and only Bill Amount shows. */}
            {!isEatapp && (
              <>
                {summaryItems.map((item, index) => (
                  <div className="my-sm flex justify-between" key={index}>
                    <div className="flex">
                      <div className="pr-md">
                        <Text className="font-bold">{item.quantity}x</Text>
                      </div>
                      <div>
                        <Text className="font-bold">{item.name}</Text>
                        {item.type === BookingItemType.PLACE && (
                          <Text className="text-gray-light">
                            {(item.adultCount ?? 0) + (item.childCount ?? 0)} {lang.guests}
                          </Text>
                        )}
                      </div>
                    </div>{" "}
                    <div className="text-right">
                      <Text className="font-bold">
                        {formatNumberToMoneyWithCurrencyParams(
                          item.totalAmount,
                          true,
                          displayCurrency
                        )}
                      </Text>
                      <Text className="text-gray-light">
                        {formatNumberToMoneyWithCurrencyParams(
                          item.itemAmount,
                          true,
                          displayCurrency
                        )}{" "}
                        / {item.type === BookingItemType.PLACE ? lang.night : lang.item}
                      </Text>
                    </div>
                  </div>
                ))}
                <Divider />
              </>
            )}
            <div className="flex justify-between items-center">
              <Text size="medium" className="font-bold">
                {lang.billAmount}
              </Text>
              <Text size="medium" className="font-bold">
                {formatNumberToMoneyWithCurrencyParams(
                  bookingDetailsData.totalBillAmount,
                  true,
                  displayCurrency
                )}
              </Text>
            </div>
          </>
        )}
      </Panel>
    </div>
  );
};

export default BookingSummary;
